export function useProductMetaLinks(product: {
  id: string
  extensions?: { novuSeoUrls?: { [key: string]: string } }
} | null) {
  const runtimeConfig = useRuntimeConfig()
  const appUrl = runtimeConfig.public.APP_URL || 'https://wetrok.ch'

  if (!product || !product.extensions?.novuSeoUrls) {
    return []
  }

  const { locales, defaultLocale } = useI18n()
  const localePath = useLocalePath()
  const route = useRoute()
  const seoUrls = product.extensions.novuSeoUrls

  // Find the default locale's shopwareLangId for x-default
  const defaultLocaleObj = (locales.value).find(
    l => l.code === defaultLocale,
  )
  const xDefaultLangId = defaultLocaleObj?.shopwareLangId
  const defaultPath = xDefaultLangId && seoUrls[xDefaultLangId]
    ? seoUrls[xDefaultLangId]
    : Object.values(seoUrls)[0] // Fallback to first path if default not found

  // Filter and map alternate links, ignoring unmatched language IDs
  const alternateLinks = Object.entries(seoUrls)
    .filter(([languageId]) => {
      const hasMatchingLocale = (locales.value).some(
        l => l.shopwareLangId === languageId,
      )
      if (!hasMatchingLocale) {
        // console.warn(
        //   `Language ID "${languageId}" in novuSeoUrls not found in i18n locales. Ignoring this URL.`,
        // )
      }
      return hasMatchingLocale // Only keep if it matches a locale
    })
    .map(([languageId, path]) => {
      const matchingLocale = (locales.value).find(
        l => l.shopwareLangId === languageId,
      )
      const hreflang = matchingLocale?.iso || (languageId === xDefaultLangId ? 'x-default' : languageId)

      // Generate translated route for this locale
      const translatedPath = localePath(
        {
          name: 'products-all',
          params: { all: path.split('/').filter(Boolean) },
        },
        matchingLocale?.code, // Use the locale code for this languageId
      )

      return {
        rel: 'alternate',
        hreflang,
        href: `${appUrl}${translatedPath}`,
      }
    })

  // Ensure x-default exists if it wasn't added in the loop
  const hasXDefault = alternateLinks.some(link => link.hreflang === 'x-default')
  if (defaultPath && !hasXDefault) {
    const translatedDefaultPath = localePath({
      name: 'products-all',
      params: {
        all: defaultPath.split('/').filter(Boolean),
      }, // Consistent cleaning
    }) // Uses current locale, adjusted below
    alternateLinks.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: `${appUrl}${translatedDefaultPath}`,
    })
  }

  // Add canonical link based on current route
  const canonicalLink = {
    rel: 'canonical',
    href: `${appUrl}${route.path}`,
  }

  return [canonicalLink, ...alternateLinks]
}
